import React, { useState } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  Typography,
  DrawerFooter,
  LoadingButton,
  Button,
  Alert,
} from '@one-thd/sui-atomic-components';
import {
  ISORISCancelRequest,
  ISORISCancelResponse,
} from '../../../../models/soris';
import { GetSORISCancelAPI } from '../../../../api/oms/order-details';
import CancelLineitemsView from './CancelLineItemsView';
import { ILineItem } from '../../../../models/common';
import {
  TriggerCancelSorisOnAbandonAnalytics,
  TriggerCancelSorisOnClickCancelAnalytics,
  TriggerCancelSorisOnErrorAnalytics,
} from '../../../../utils/analytics';
import { TrackEditDeliveryAddressAnalytics } from '../../../../utils/edit-delivery-address/analytics';

interface CancelConfirmationDrawerProps {
  displayDrawer: boolean;
  closeDrawer: () => void;
  lineItems: ILineItem[];
  sorisReturnCancelRequest: ISORISCancelRequest;
}

export const ERROR_MESSAGE =
  'There was a temporary technical error. Please try again shortly. If the issue persists, please call Customer Support at 1-800-430-3376.';
const SUCCESS_MESSAGE =
  "We've cancelled your return. You can start a new return for items still within their open return window.";

const CancelConfirmationDrawer = ({
  displayDrawer,
  closeDrawer,
  lineItems,
  sorisReturnCancelRequest,
}: CancelConfirmationDrawerProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [apiResponse, setAPIResponse] = useState<
    ISORISCancelResponse | undefined
  >();

  // Refreshing the page so the updated order details can reflect once cancel is successful
  const navigateToUpdatedOrderDetails = () => window.location.reload();

  const onClick = () => {
    if (apiResponse) {
      TrackEditDeliveryAddressAnalytics(
        'purchase history: order details: edit delivery address order details button',
        'button submission',
      );
      navigateToUpdatedOrderDetails();
      return;
    }
    handleCancelClick();
  };

  const onClose = () => {
    // We dont want to close the drawer if the request is still loading
    if (loading) return;
    // If the user tries to navigate away from the drawer after a successful cancel we want to refresh the page
    if (apiResponse) navigateToUpdatedOrderDetails();

    // Want to capture this analytics if user did not cancel the return and closed the drawer
    TriggerCancelSorisOnAbandonAnalytics();

    // Resetting states when the drawer is closed
    setLoading(false);
    setError(false);
    setAPIResponse(undefined);

    closeDrawer();
  };

  const handleCancelClick = () => {
    setLoading(true);
    setError(false);
    TriggerCancelSorisOnClickCancelAnalytics();

    return GetSORISCancelAPI(sorisReturnCancelRequest)
      .then((response) => {
        if (!response.data?.orderId) {
          throw new Error('No orderId or tracking number found');
        }
        setAPIResponse(response.data as ISORISCancelResponse);
      })
      .catch(() => {
        TriggerCancelSorisOnErrorAnalytics();
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="sui-flex sui-flex-col sui-full sui-gap-4 sui-w-96">
      <Drawer
        open={displayDrawer}
        onClose={onClose}
        position="right"
        className="drawer-z-index-fix"
      >
        <DrawerHeader title="Cancel This Return?" onClose={onClose} />
        <DrawerBody style={{ 'padding-bottom': '0' }}>
          {error && (
            <Alert status="error" size="large">
              {ERROR_MESSAGE}
            </Alert>
          )}
          {apiResponse && (
            <Alert status="success" size="large">
              {SUCCESS_MESSAGE}
            </Alert>
          )}
          <Typography variant="body-base" style={{ marginBottom: '2.4rem' }}>
            Review the items below that you want to keep. You can still start a
            return for these items within their return window.
          </Typography>
          <CancelLineitemsView lineItems={lineItems} />
        </DrawerBody>
        <DrawerFooter className="sui-bg-primary sui-p-4 sui-relative sui-bottom-0 sui-sticky sui-shadow-sm">
          <div className="sui-flex sui-flex-col sui-min-w-full sui-gap-4">
            <LoadingButton
              loading={loading}
              fullWidth
              style={{ width: '100%' }}
              onClick={onClick}
            >
              {apiResponse
                ? 'Back to Order Details'
                : 'Yes, Cancel This Return'}
            </LoadingButton>
            {!apiResponse && (
              <Button
                fullWidth
                style={{ width: '100%' }}
                variant="secondary"
                onClick={onClose}
              >
                No
              </Button>
            )}
          </div>
        </DrawerFooter>
      </Drawer>
    </div>
  );
};

export default CancelConfirmationDrawer;
